// Paint Bucket Tool (G) — fills an area with the foreground color.
// Uses flood-fill to find contiguous (or all) pixels within tolerance.
//
// Photoshop parity:
// - Tolerance (0-255)
// - Contiguous toggle
// - Anti-alias toggle (softens edges)
// - Fill type: foreground color or a tiled pattern
// - Sample All Layers — the similarity test considers a composite of every
//   visible layer; the fill itself still only writes to the active layer
//   (matches Photoshop)
// - Mode (blend mode) — how the fill composites onto the layer
// - Opacity control

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import type { BlendMode, Layer, RasterLayer } from "../document/Layer";
import { collectRasterLayers, findRaster } from "../document/LayerUtils";
import { compositeLayer } from "../pixel/blend";
import type { BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export class PaintBucketTool implements Tool {
  readonly id = "paint-bucket";
  readonly shortcut = "G";

  tolerance = 32;
  contiguous = true;
  antiAlias = true;
  opacity = 1;
  /** Options-bar "Mode" — how the fill composites onto the layer's existing pixels. */
  blendMode: BlendMode = "normal";
  /**
   * When true, the flood-fill similarity test samples a composite of every
   * visible layer instead of just the active layer's own pixels — so you
   * can fill a region on a transparent/blank layer by clicking a boundary
   * that's only visible on the layers below. The fill itself always writes
   * to the active layer only (matches Photoshop's Paint Bucket).
   */
  sampleAllLayers = false;
  /** Fill source: a flat foreground color, or a tiled pattern. */
  fillSource: "foreground" | "pattern" = "foreground";
  /** Pattern to tile when fillSource === "pattern" (loaded via the pattern picker). */
  pattern: { pixels: Uint8ClampedArray; width: number; height: number } | null = null;
  /** Identifier of the currently-loaded pattern, for UI display/round-tripping. */
  patternId: string | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    // Capture BEFORE state before fill mutates pixels.
    const beforeData = new Uint8ClampedArray(layer.pixels.data);

    const bbox = this.fill(ctx, layer, docX, docY);
    if (!bbox) return;

    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(beforeData, layer.pixels.width, bbox);
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  /** Resolve the flat fill color, or the tiled pattern color at (px,py), for pixel-level source color. */
  private sourceColorAt(ctx: ToolContext, px: number, py: number): { r: number; g: number; b: number } {
    if (this.fillSource === "pattern" && this.pattern) {
      const patW = this.pattern.width;
      const patH = this.pattern.height;
      const pxMod = ((px % patW) + patW) % patW;
      const pyMod = ((py % patH) + patH) % patH;
      const pi = (pyMod * patW + pxMod) * 4;
      return {
        r: this.pattern.pixels[pi]!,
        g: this.pattern.pixels[pi + 1]!,
        b: this.pattern.pixels[pi + 2]!,
      };
    }
    return { r: ctx.fgColor.r, g: ctx.fgColor.g, b: ctx.fgColor.b };
  }

  private fill(
    ctx: ToolContext,
    layer: RasterLayer,
    seedX: number,
    seedY: number,
  ): BBox | null {
    const data = layer.pixels.data;
    const w = layer.pixels.width;
    const h = layer.pixels.height;

    // Clamp seed to layer bounds
    const sx = Math.max(0, Math.min(w - 1, Math.round(seedX)));
    const sy = Math.max(0, Math.min(h - 1, Math.round(seedY)));

    // The similarity test compares against this buffer: the active layer's
    // own pixels normally, or a composite of every visible layer when
    // Sample All Layers is on (audit finding 190). The fill always writes
    // to `data` (the active layer) regardless of which buffer we compare
    // against.
    const compareData = (this.sampleAllLayers && ctx.doc)
      ? buildLayerLocalComposite(ctx.doc, layer)
      : data;

    // Get the seed pixel color
    const seedIdx = (sy * w + sx) * 4;
    const seedR = compareData[seedIdx]!;
    const seedG = compareData[seedIdx + 1]!;
    const seedB = compareData[seedIdx + 2]!;
    const seedA = compareData[seedIdx + 3]!;

    // Get selection mask for constraining fill
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    const selMask = sel?.mask;

    const tol = this.tolerance;
    const fillA = Math.round(this.opacity * 255);

    // Flood fill to find all matching pixels
    const mask = new Uint8Array(w * h); // 0 = not filled, 1 = filled, 2 = edge (for AA)
    const stack: number[] = [sy * w + sx];
    const visited = new Uint8Array(w * h);

    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    if (this.contiguous) {
      // Contiguous flood fill
      while (stack.length > 0) {
        const pos = stack.pop()!;
        if (visited[pos]) continue;
        visited[pos] = 1;

        const px = pos % w;
        const py = (pos - px) / w;

        // Selection masking: skip pixels outside selection
        if (selMask && selMask[py * w + px] === 0) continue;

        const idx = pos * 4;
        const dr = compareData[idx]! - seedR;
        const dg = compareData[idx + 1]! - seedG;
        const db = compareData[idx + 2]! - seedB;
        const da = compareData[idx + 3]! - seedA;

        // Check if pixel matches seed within tolerance
        if (Math.abs(dr) > tol || Math.abs(dg) > tol || Math.abs(db) > tol || Math.abs(da) > tol) {
          // Check if this is an edge pixel (adjacent to matching pixels) for AA
          if (this.antiAlias) {
            mask[pos] = 2; // edge
          }
          continue;
        }

        mask[pos] = 1;

        if (px < touchX0) touchX0 = px;
        if (py < touchY0) touchY0 = py;
        if (px > touchX1) touchX1 = px;
        if (py > touchY1) touchY1 = py;

        // Add neighbors
        if (px > 0) stack.push(pos - 1);
        if (px < w - 1) stack.push(pos + 1);
        if (py > 0) stack.push(pos - w);
        if (py < h - 1) stack.push(pos + w);
      }
    } else {
      // Non-contiguous: scan entire layer (constrained by selection if one exists)
      for (let py = 0; py < h; py++) {
        for (let px = 0; px < w; px++) {
          const pos = py * w + px;
          // Skip pixels outside selection
          if (selMask && selMask[pos] === 0) continue;
          const idx = pos * 4;
          const dr = compareData[idx]! - seedR;
          const dg = compareData[idx + 1]! - seedG;
          const db = compareData[idx + 2]! - seedB;
          const da = compareData[idx + 3]! - seedA;

          if (Math.abs(dr) <= tol && Math.abs(dg) <= tol && Math.abs(db) <= tol && Math.abs(da) <= tol) {
            mask[pos] = 1;
            if (px < touchX0) touchX0 = px;
            if (py < touchY0) touchY0 = py;
            if (px > touchX1) touchX1 = px;
            if (py > touchY1) touchY1 = py;
          } else if (this.antiAlias) {
            // Check if adjacent to a matching pixel
            let adjacent = false;
            if (px > 0) {
              const nIdx = (pos - 1) * 4;
              if (Math.abs(compareData[nIdx]! - seedR) <= tol && Math.abs(compareData[nIdx + 1]! - seedG) <= tol &&
                  Math.abs(compareData[nIdx + 2]! - seedB) <= tol && Math.abs(compareData[nIdx + 3]! - seedA) <= tol) {
                adjacent = true;
              }
            }
            if (!adjacent && px < w - 1) {
              const nIdx = (pos + 1) * 4;
              if (Math.abs(compareData[nIdx]! - seedR) <= tol && Math.abs(compareData[nIdx + 1]! - seedG) <= tol &&
                  Math.abs(compareData[nIdx + 2]! - seedB) <= tol && Math.abs(compareData[nIdx + 3]! - seedA) <= tol) {
                adjacent = true;
              }
            }
            if (!adjacent && py > 0) {
              const nIdx = (pos - w) * 4;
              if (Math.abs(compareData[nIdx]! - seedR) <= tol && Math.abs(compareData[nIdx + 1]! - seedG) <= tol &&
                  Math.abs(compareData[nIdx + 2]! - seedB) <= tol && Math.abs(compareData[nIdx + 3]! - seedA) <= tol) {
                adjacent = true;
              }
            }
            if (!adjacent && py < h - 1) {
              const nIdx = (pos + w) * 4;
              if (Math.abs(compareData[nIdx]! - seedR) <= tol && Math.abs(compareData[nIdx + 1]! - seedG) <= tol &&
                  Math.abs(compareData[nIdx + 2]! - seedB) <= tol && Math.abs(compareData[nIdx + 3]! - seedA) <= tol) {
                adjacent = true;
              }
            }
            if (adjacent) {
              mask[pos] = 2; // edge
            }
          }
        }
      }
    }

    if (touchX1 < 0) return null;

    // Build the fill's source RGBA for the touched bbox, then composite it
    // onto the layer's pixels using the selected blend mode (audit finding
    // 190) — previously the fill always did a plain alpha-over blend
    // regardless of `blendMode`.
    const bboxW = touchX1 - touchX0 + 1;
    const bboxH = touchY1 - touchY0 + 1;
    const srcRegion = new Uint8ClampedArray(bboxW * bboxH * 4);
    const dstRegion = new Uint8ClampedArray(bboxW * bboxH * 4);
    const lockT = layer.lockTransparency;

    for (let py = touchY0; py <= touchY1; py++) {
      for (let px = touchX0; px <= touchX1; px++) {
        const pos = py * w + px;
        const ri = ((py - touchY0) * bboxW + (px - touchX0)) * 4;
        const idx = pos * 4;
        dstRegion[ri] = data[idx]!;
        dstRegion[ri + 1] = data[idx + 1]!;
        dstRegion[ri + 2] = data[idx + 2]!;
        dstRegion[ri + 3] = data[idx + 3]!;

        // Skip pixels outside selection, unmasked pixels, and (when locked)
        // fully transparent pixels — leave srcRegion alpha at 0 so
        // compositeLayer() passes the destination through unchanged.
        if (selMask && selMask[pos] === 0) continue;
        const m = mask[pos];
        if (m === 0) continue;
        if (lockT && data[idx + 3] === 0) continue;

        const { r: fillR, g: fillG, b: fillB } = this.sourceColorAt(ctx, px, py);

        let srcAlpha = fillA;
        if (m === 2) {
          if (!this.antiAlias) continue;
          // Edge pixel: partial fill based on how close it is to the tolerance boundary
          const dr = compareData[idx]! - seedR;
          const dg = compareData[idx + 1]! - seedG;
          const db = compareData[idx + 2]! - seedB;
          const da = compareData[idx + 3]! - seedA;
          const maxDiff = Math.max(Math.abs(dr), Math.abs(dg), Math.abs(db), Math.abs(da));
          const overshoot = Math.min(1, (maxDiff - tol) / tol);
          srcAlpha = Math.round((1 - overshoot) * fillA);
        }

        srcRegion[ri] = fillR;
        srcRegion[ri + 1] = fillG;
        srcRegion[ri + 2] = fillB;
        srcRegion[ri + 3] = srcAlpha;
      }
    }

    compositeLayer(dstRegion, srcRegion, this.blendMode, 1);

    for (let py = touchY0; py <= touchY1; py++) {
      for (let px = touchX0; px <= touchX1; px++) {
        const idx = (py * w + px) * 4;
        const ri = ((py - touchY0) * bboxW + (px - touchX0)) * 4;
        data[idx] = dstRegion[ri]!;
        data[idx + 1] = dstRegion[ri + 1]!;
        data[idx + 2] = dstRegion[ri + 2]!;
        data[idx + 3] = dstRegion[ri + 3]!;
      }
    }

    layer.pixels.markDirty();

    return {
      x: touchX0, y: touchY0,
      w: touchX1 - touchX0 + 1,
      h: touchY1 - touchY0 + 1,
    };
  }
}

/**
 * Build a buffer the same size as `layer.pixels` holding, at each layer-local
 * pixel, the color from a composite of every visible raster layer in `doc`
 * at the corresponding document coordinate (or transparent black if that
 * doc coordinate falls outside every layer). Used as the flood-fill
 * similarity-test source when Sample All Layers is on — the fill itself
 * still only ever writes to `layer.pixels`.
 */
function buildLayerLocalComposite(
  doc: { layers: readonly Layer[]; width: number; height: number },
  layer: RasterLayer,
): Uint8ClampedArray {
  const docW = doc.width;
  const docH = doc.height;
  const composite = new Uint8ClampedArray(docW * docH * 4);

  const rasters = collectRasterLayers(doc.layers);
  for (const l of rasters) {
    if (!l.visible || l.opacity <= 0) continue;
    const lw = l.pixels.width;
    const lh = l.pixels.height;
    if (lw <= 0 || lh <= 0) continue;

    const x0 = Math.max(0, l.x);
    const y0 = Math.max(0, l.y);
    const x1 = Math.min(docW, l.x + lw);
    const y1 = Math.min(docH, l.y + lh);
    if (x1 <= x0 || y1 <= y0) continue;

    const placed = new Uint8ClampedArray(docW * docH * 4);
    for (let y = y0; y < y1; y++) {
      const srcRowStart = ((y - l.y) * lw + (x0 - l.x)) * 4;
      const dstRowStart = (y * docW + x0) * 4;
      const rowLen = (x1 - x0) * 4;
      placed.set(l.pixels.data.subarray(srcRowStart, srcRowStart + rowLen), dstRowStart);
    }
    compositeLayer(composite, placed, l.blendMode, l.opacity);
  }

  const lw = layer.pixels.width;
  const lh = layer.pixels.height;
  const out = new Uint8ClampedArray(lw * lh * 4);
  for (let py = 0; py < lh; py++) {
    const docY = py + layer.y;
    for (let px = 0; px < lw; px++) {
      const docX = px + layer.x;
      const outIdx = (py * lw + px) * 4;
      if (docX < 0 || docY < 0 || docX >= docW || docY >= docH) continue; // stays transparent
      const srcIdx = (docY * docW + docX) * 4;
      out[outIdx] = composite[srcIdx]!;
      out[outIdx + 1] = composite[srcIdx + 1]!;
      out[outIdx + 2] = composite[srcIdx + 2]!;
      out[outIdx + 3] = composite[srcIdx + 3]!;
    }
  }
  return out;
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}
